import { HttpBackend, HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AppService } from "../../../core/services/app.service";

export type SubscriptionOrderType = "monthly" | "annual";

export interface CreateSubscriptionRequest {
  email: string;
  digitalMemoryLogId: number;
  type: SubscriptionOrderType;
  /** Stripe unit_amount (smallest currency unit), e.g. USD 9.99 → 999 */
  price: number;
  currency: string;
}

export interface SubscriptionOrder {
  id?: number;
  email?: string;
  expiredAt?: string;
  amount?: number;
  currency?: string;
  status?: string;
  digitalMemoryLogId?: number;
  type?: SubscriptionOrderType;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripePaymentIntentId?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface CreateSubscriptionResponse {
  order?: SubscriptionOrder;
  clientSecret?: string;
  client_secret?: string;
  subscriptionId?: string;
  url?: string;
  amount?: number;
  currency?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: "root",
})
export class OrderService {
  private apiUrl = AppService.getApiUrl() + "/api/Orders";
  private rawHttp: HttpClient;

  constructor(private http: HttpClient, httpBackend: HttpBackend) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  createSubscription(
    payload: CreateSubscriptionRequest,
    requireAuth = false
  ): Observable<CreateSubscriptionResponse> {
    const url = this.apiUrl + "/create-subscription";
    const request = requireAuth ? this.http.post(url, payload) : this.rawHttp.post(url, payload);

    return request.pipe(
      map((res: any) => {
        if (res && res.IsSuccess === false) {
          throw res;
        }
        return (res && res.Data) || res;
      })
    );
  }

  extractClientSecret(session: CreateSubscriptionResponse | null | undefined): string {
    if (!session) {
      return "";
    }

    if (session.clientSecret) {
      return session.clientSecret;
    }

    if (session.client_secret) {
      return session.client_secret;
    }

    const nestedPaymentIntent = session.paymentIntent || session.payment_intent;
    if (nestedPaymentIntent) {
      return nestedPaymentIntent.clientSecret || nestedPaymentIntent.client_secret || "";
    }

    const nestedSubscription = session.subscription;
    if (nestedSubscription && nestedSubscription.latest_invoice) {
      const invoice = nestedSubscription.latest_invoice;
      const paymentIntent = invoice.payment_intent || invoice.paymentIntent;
      if (paymentIntent) {
        return paymentIntent.client_secret || paymentIntent.clientSecret || "";
      }
    }

    return "";
  }
}
